import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    SCORECARD_REPORT,
    BUTTONS,
    SUCCESS,
    ERROR,
    MAX_LENGTH,
    QUERY_KEYS,
} from '../../../constants';
import { getHiringClients, popup, updatePlatform } from '../../../utils';
import { handleNotesChangeFn, handleSaveFn } from './ScoreCardsFunctions';
import SSCSelect from '../../shared/SSCSelect';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';
import { CircleSpinner } from '../../../assets/svg';

const EditScorecardModal = ({
    accountDetails,
    openEditModal,
    setOpenEditModal,
    selectedData,
    isEdit,
    selectedPlatform,
    scorecards,
}) => {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedHiringClient, setSelectedHiringClient] = useState(null);
    const [notes, setNotes] = useState('');
    const [selectedHiringClientGrade, setSelectedHiringClientGrade] = useState(null);

    const handleHiringClientSelect = (hc) => {
        setSearchQuery('');
        setSelectedHiringClient(hc);
        hc?.hiring_client_grade && setSelectedHiringClientGrade(hc.hiring_client_grade);
    };
    const handleNotes = handleNotesChangeFn(setNotes);

    const [debouncedSearch] = useDebouncedValue(searchQuery, 500);
    const { data: hiringClients, isFetching: hcFetching } = useQuery({
        queryKey: [
            QUERY_KEYS.HIRING_CLIENTS,
            accountDetails?.id,
            selectedPlatform?.id,
            debouncedSearch,
        ],
        queryFn: async () =>
            await getHiringClients({
                account_id: accountDetails?.id,
                platform_id: selectedPlatform?.id,
                search: debouncedSearch,
                excludesScorecardHCs: true,
            }),
        select: (data) => {
            const ids = new Set(scorecards?.map((item) => item.hiring_client_id));
            const filteredHC = data?.data?.filter((item) => {
                return !ids.has(item.id);
            });
            return filteredHC.map((hc) => ({ label: hc.name, value: hc }));
        },
    });

    const handleSaveMutation = useMutation({
        mutationFn: async () =>
            await handleSaveFn({
                isEdit,
                selectedHiringClient,
                selectedPlatform,
                notes,
                selectedData,
                setOpenEditModal,
                accountId: accountDetails?.id,
                platform_grade: accountDetails?.grade,
                scorecards,
                selectedHiringClientGrade,
            }),
        onSuccess: async (res) => {
            if (res.success) {
                if (!isEdit && !scorecards.length) {
                    const data = {
                        platform_grade: accountDetails?.grade,
                        account_id: accountDetails?.id,
                    };
                    await updatePlatform({
                        id: accountDetails?.platform_id.toString(),
                        data: data,
                    });
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.SCORECARD_LIST],
                        exact: false,
                    });
                }
                setTimeout(() => {
                    res.type === 'create'
                        ? popup('success', SUCCESS.SCORE_CARD_COLUMNS_ADMIN.CREATED)
                        : popup('success', SUCCESS.SCORE_CARD_COLUMNS_ADMIN.UPDATED);
                }, 250);
                setOpenEditModal(false);
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.SCORECARD_DETAILS],
                    exact: false,
                });
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.HIRING_CLIENTS],
                    exact: false,
                });
            }
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const handleSave = async () => {
        if (selectedHiringClient?.id) {
            handleSaveMutation.mutate();
        } else {
            const err_message = !selectedHiringClient?.id
                ? ERROR.SELECT_HIRING_CLIENT
                : ERROR.UNEXPECTED_ERROR;
            popup('error', err_message);
        }
    };

    useEffect(() => {
        if (selectedData) {
            const { hiring_client_id, hiring_client_name, notes, hc_grade } = selectedData;
            setSelectedHiringClient({ name: hiring_client_name, id: hiring_client_id });
            setSelectedHiringClientGrade(hc_grade);
            setNotes(notes);
        }
    }, [selectedData]);

    return (
        <div
            className={`modal custom-modal-doc-cato fade ${openEditModal ? 'show' : ''}`}
            tabIndex={-1}
            aria-labelledby='exampleModalLabel'
            aria-hidden={!openEditModal}
            role='dialog'
            style={{ display: openEditModal ? 'block' : 'none' }}
        >
            <div className='modal-dialog w-100'>
                <div className='d-flex flex-column gap-4 modal-content border-0'>
                    <div className='d-flex align-items-center justify-content-between'>
                        <span className='head-title-edit'>
                            {isEdit ? SCORECARD_REPORT.EDIT_BUTTON : SCORECARD_REPORT.BUTTON_TEXT}
                        </span>
                        <button
                            type='button'
                            onClick={() => setOpenEditModal(false)}
                            className='btn-close'
                        ></button>
                    </div>
                    <div className='d-flex flex-column gap-3 modal-body p-0 ssc-scorecard-form'>
                        <SSCSelect
                            label={SCORECARD_REPORT.COLUMNS.HIRING_CLIENT.NAME}
                            placeholder={SCORECARD_REPORT.COLUMNS.HIRING_CLIENT.PLACEHOLDER}
                            isLoading={hcFetching}
                            isDisabled={isEdit}
                            isClearable={true}
                            isOptionSelected={(data) =>
                                data?.value?.id === selectedHiringClient?.id
                            }
                            value={
                                isEmpty(selectedHiringClient)
                                    ? null
                                    : {
                                          label: selectedHiringClient?.name,
                                          value: selectedHiringClient,
                                      }
                            }
                            options={hiringClients}
                            onChange={(data) => handleHiringClientSelect(data?.value)}
                            onInputChange={(v) => {
                                if (!isEmpty(v)) {
                                    setSearchQuery(v.trim());
                                }
                            }}
                            required
                        />
                        <div className=' d-flex align-items-start flex-column'>
                            <label htmlFor=''>{SCORECARD_REPORT.COLUMNS.NOTES.NAME}</label>
                            <textarea
                                onChange={handleNotes}
                                value={notes || ''}
                                name=''
                                id=''
                                cols='20'
                                rows='3'
                                className='add-newsfeed-text w-100'
                                placeholder={SCORECARD_REPORT.COLUMNS.NOTES.PLACEHOLDER}
                            ></textarea>
                        </div>
                        <div className='d-flex align-items-start flex-column'>
                            <label htmlFor=''>{SCORECARD_REPORT.COLUMNS.HIRING_CLIENT_GRADE}</label>
                            <input
                                maxLength={MAX_LENGTH.HIRING_CLIENT_GRADE}
                                type='text'
                                name='grade'
                                onChange={(e) => setSelectedHiringClientGrade(e.target.value)}
                                value={selectedHiringClientGrade || ''}
                                className={`add-grade-hc-text w-100`}
                                placeholder={SCORECARD_REPORT.COLUMNS.GRADE.PLACEHOLDER}
                            />
                        </div>
                    </div>
                    <div className='d-flex align-items-center justify-content-end modal-btn mt-2'>
                        <button
                            type='button'
                            onClick={() => setOpenEditModal(false)}
                            className='ssc-secondary-white-btn'
                            disabled={handleSaveMutation.isPending}
                        >
                            {BUTTONS.CANCEL_BUTTON}
                        </button>

                        <button
                            type='button'
                            onClick={handleSave}
                            className='ssc-primary-green-btn py-0 w-auto'
                            disabled={isEmpty(selectedHiringClient) || handleSaveMutation.isPending}
                        >
                            {handleSaveMutation.isPending && (
                                <span className='me-1'>
                                    <CircleSpinner />
                                </span>
                            )}
                            {BUTTONS.SAVE_BUTTON}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditScorecardModal;
EditScorecardModal.propTypes = {
    accountDetails: PropTypes.object,
    openEditModal: PropTypes.bool,
    setOpenEditModal: PropTypes.func,
    selectedItemId: PropTypes.string,
    isEdit: PropTypes.bool,
    setResponse: PropTypes.func,
    selectedData: PropTypes.object,
    selectedPlatform: PropTypes.object,
    scorecards: PropTypes.array,
};
